import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, RadioButtonUnchecked, FiberManualRecord, Cancel } from '@mui/icons-material';
import { t } from 'i18next';
import { setMobileTextColor } from '../utility';

const OrderStatusTracker = ({ statuses, textColor = '#000', accentColor = '#4CAF50', isMobile = true }) => {
    // Filter only statuses that should be shown
    const visibleStatuses = statuses.filter(status => status.show);

    // Check if order is cancelled
    const cancelledStatus = statuses.find(status => status.name === 'cancelled' && status.done);

    if (cancelledStatus) {
        return (
            <div className='sub-font' style={{
                padding: isMobile ? '16px 12px' : '20px',
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
            }}>
                <Cancel sx={{ fontSize: isMobile ? 32 : 40, color: setMobileTextColor() }} />
                <div>
                    <h3 style={{
                        margin: 0,
                        color: setMobileTextColor(),
                        fontSize: isMobile ? '16px' : '18px',
                        fontWeight: 'bold'
                    }}>
                        {t('Order Cancelled')}
                    </h3>
                    {cancelledStatus.date && (
                        <p className='sub-font' style={{
                            margin: '4px 0 0 0',
                            color: setMobileTextColor(),
                            fontSize: isMobile ? '12px' : '14px'
                        }}>
                            {new Date(cancelledStatus.date).toLocaleString([], {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    const getStatusState = (status) => {
        if (status.date && status.done) {
            return 'completed';
        } else if (status.date && !status.done) {
            return 'current';
        } else {
            return 'pending';
        }
    };

    const getStatusIcon = (state) => {
        const iconSize = isMobile ? 28 : 32;
        switch (state) {
            case 'completed':
                return <CheckCircle sx={{ fontSize: iconSize }} />;
            case 'current':
                return <FiberManualRecord sx={{ fontSize: iconSize }} />;
            case 'pending':
                return (
                    <div style={{
                        width: iconSize,
                        height: iconSize,
                        borderRadius: '50%',
                        border: '3px solid currentColor',
                        position: 'relative'
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: 'currentColor',
                            opacity: 0.3
                        }} />
                    </div>
                );
            default:
                return <RadioButtonUnchecked sx={{ fontSize: iconSize }} />;
        }
    };

    const getStatusColor = (state) => {
        switch (state) {
            case 'completed':
                return accentColor;
            case 'current':
                return '#FF9800';
            case 'pending':
                return '#BDBDBD';
            default:
                return '#BDBDBD';
        }
    };

    return (
        <div style={{
            padding: isMobile ? '20px 0px' : '24px 16px',
            backgroundColor: 'transparent',
            width: '100%'
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                width: '100%',
                position: 'relative',
                paddingBottom: '10px'
            }}>
                {visibleStatuses.map((status, index) => {
                    const state = getStatusState(status);
                    const color = getStatusColor(state);
                    const isLast = index === visibleStatuses.length - 1;

                    return (
                        <React.Fragment key={index}>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.5 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{
                                    duration: 0.5,
                                    delay: index * 0.1,
                                    type: "spring",
                                    stiffness: 200
                                }}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    position: 'relative',
                                    zIndex: 1,
                                    minWidth: isMobile ? '53px' : '80px',
                                    flex: 1
                                }}
                            >
                                <motion.div
                                    animate={state === 'current' ? {
                                        scale: [1, 1.15, 1],
                                    } : {}}
                                    transition={{
                                        duration: 1.5,
                                        repeat: state === 'current' ? Infinity : 0,
                                        ease: "easeInOut"
                                    }}
                                    style={{
                                        color: color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: state === 'pending'
                                            ? 'rgba(189, 189, 189, 0.15)'
                                            : state === 'current'
                                                ? 'rgba(255, 152, 0, 0.15)'
                                                : `${accentColor}15`,
                                        borderRadius: '50%',
                                        padding: isMobile ? '4px' : '10px',
                                        position: 'relative',
                                        border: state === 'pending' ? `2px dashed ${color}` : 'none',
                                        boxShadow: state !== 'pending' ? '0 4px 12px rgba(0,0,0,0.1)' : 'none'
                                    }}
                                >
                                    {getStatusIcon(state)}

                                    {/* Pulse animation for current status */}
                                    {state === 'current' && (
                                        <>
                                            <motion.div
                                                animate={{
                                                    scale: [1, 2.2, 2.2],
                                                    opacity: [0.6, 0, 0]
                                                }}
                                                transition={{
                                                    duration: 1.5,
                                                    repeat: Infinity,
                                                    ease: "easeOut"
                                                }}
                                                style={{
                                                    position: 'absolute',
                                                    width: '100%',
                                                    height: '100%',
                                                    borderRadius: '50%',
                                                    backgroundColor: color,
                                                    zIndex: -1
                                                }}
                                            />
                                            <motion.div
                                                animate={{
                                                    rotate: 360
                                                }}
                                                transition={{
                                                    duration: 3,
                                                    repeat: Infinity,
                                                    ease: "linear"
                                                }}
                                                style={{
                                                    position: 'absolute',
                                                    width: '120%',
                                                    height: '120%',
                                                    borderRadius: '50%',
                                                    border: `2px solid ${color}`,
                                                    borderTopColor: 'transparent',
                                                    borderRightColor: 'transparent',
                                                    zIndex: -1,
                                                    opacity: 0.5
                                                }}
                                            />
                                        </>
                                    )}

                                    {state === 'completed' && (
                                        <motion.div
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            transition={{
                                                delay: index * 0.1 + 0.3,
                                                type: "spring",
                                                stiffness: 300,
                                                damping: 15
                                            }}
                                            style={{
                                                position: 'absolute',
                                                inset: 0
                                            }}
                                        />
                                    )}
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 + 0.2 }}
                                    style={{
                                        marginTop: '10px',
                                        fontSize: isMobile ? '11px' : '13px',
                                        fontWeight: state === 'current' ? 'bold' : state === 'completed' ? '600' : '500',
                                        color: state === 'pending' ? '#999' : textColor,
                                        textAlign: 'center',
                                        whiteSpace: 'normal',
                                        textTransform: 'capitalize',
                                        lineHeight: '1.3',
                                        maxWidth: '100%'
                                    }}
                                    className='sub-font'
                                >
                                    {t(status.name)}
                                </motion.div>

                                {/* Date and Time */}
                                {status.date && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: index * 0.1 + 0.3 }}
                                        style={{
                                            marginTop: '6px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '2px'
                                        }}
                                        className='sub-font'
                                    >
                                        <div style={{
                                            fontSize: isMobile ? '9px' : '10px',
                                            color: state === 'current' ? color : setMobileTextColor(),
                                            fontWeight: state === 'current' ? 'bold' : 'normal',
                                            textAlign: 'center'
                                        }}>
                                            {new Date(status.date).toLocaleTimeString([], {
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </div>
                                        <div className='sub-font' style={{
                                            fontSize: isMobile ? '8px' : '9px',
                                            color: state === 'current' ? color : setMobileTextColor(),
                                            textAlign: 'center'

                                        }}>
                                            {new Date(status.date).toLocaleDateString([], {
                                                month: 'short',
                                                day: 'numeric'
                                            })}
                                        </div>
                                    </motion.div>
                                )}
                            </motion.div>

                            {/* Connecting Line */}
                            {!isLast && (
                                <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'relative',
                                    height: '100%',
                                    paddingTop: isMobile ? '20px' : '24px',
                                    minWidth: isMobile ? '20px' : '30px',
                                    maxWidth: isMobile ? '60px' : '80px'
                                }}>
                                    <motion.div
                                        initial={{ scaleX: 0 }}
                                        animate={{ scaleX: 1 }}
                                        transition={{
                                            duration: 0.6,
                                            delay: index * 0.1 + 0.3,
                                            ease: "easeInOut"
                                        }}
                                        style={{
                                            width: '100%',
                                            height: state === 'pending' ? '2px' : '4px',
                                            backgroundColor: state === 'completed' ? color : '#E0E0E0',

                                            borderRadius: '2px',
                                            transformOrigin: 'left',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            border: state === 'pending' ? '1px dashed #E0E0E0' : 'none',
                                            background: state === 'pending'
                                                ? 'transparent'
                                                : state === 'completed'
                                                    ? color
                                                    : '#E0E0E0'
                                        }}
                                    >
                                        {/* Animated gradient for current status line */}
                                        {state === 'current' && (
                                            <motion.div
                                                animate={{
                                                    x: ['-100%', '200%']
                                                }}
                                                transition={{
                                                    duration: 2,
                                                    repeat: Infinity,
                                                    ease: "linear"
                                                }}
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    background: `linear-gradient(90deg, transparent, ${color}, ${color}, transparent)`,
                                                    width: '50%'
                                                }}
                                            />
                                        )}
                                    </motion.div>
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};

export default OrderStatusTracker;
